import type { TFunction } from "i18next";
import { z } from "zod";
import { SUPPORTED_LOCALES } from "@/lib/locales";
import { isValidPhone, normalizePhone } from "@/lib/phoneValidation";
import type { LegalFieldPolicy } from "@/services/profileService";
import { isFieldExcluded, isFieldOptional } from "./profileFormTypes";

const CONTACT_PHONE_MAX_LEN = 20;

function currentYear(): number {
  return new Date().getFullYear();
}

/** Holders must be between 18 and 110 years old. */
function birthYearRange(year: number): { minYear: number; maxYear: number } {
  return { minYear: year - 110, maxYear: year - 18 };
}

function documentYearRange(year: number): { minYear: number; maxYear: number } {
  return { minYear: 2000, maxYear: year + 30 };
}

/** True when ``val`` is a real calendar YYYY-MM-DD (matches backend ``date.fromisoformat``). */
function isStrictIsoDate(val: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

function requiredBirthDateSchema(t: TFunction<"validation">) {
  const field = t("fields.birthDate");
  return z
    .string()
    .min(1, t("fieldRequired", { field }))
    .refine(
      (val) => {
        if (!isStrictIsoDate(val)) return false;

        const year = parseInt(val.substring(0, 4), 10);
        const { minYear, maxYear } = birthYearRange(currentYear());

        return year >= minYear && year <= maxYear;
      },
      {
        message: t("birthDateInvalid", birthYearRange(currentYear())),
      },
    );
}

function requiredDocumentDateSchema(
  t: TFunction<"validation">,
  fieldLabel: string,
) {
  return z
    .string()
    .min(1, t("fieldRequired", { field: fieldLabel }))
    .refine(
      (val) => {
        if (!isStrictIsoDate(val)) return false;
        const year = parseInt(val.substring(0, 4), 10);
        const { minYear, maxYear } = documentYearRange(currentYear());

        return year >= minYear && year <= maxYear;
      },
      {
        message: t("documentDateInvalid", {
          maxYear: documentYearRange(currentYear()).maxYear,
        }),
      },
    );
}

function requiredTrimmedString(
  t: TFunction<"validation">,
  label: string,
  min: number,
  max: number,
) {
  const trimmed = z.string().transform((s) => s.trim());
  if (min <= 1) {
    return trimmed.pipe(
      z
        .string()
        .min(1, t("fieldRequired", { field: label }))
        .max(max, t("fieldMaxChars", { field: label, max })),
    );
  }
  return trimmed.pipe(
    z
      .string()
      .min(min, t("fieldMinChars", { field: label, min }))
      .max(max, t("fieldMaxChars", { field: label, max })),
  );
}

export function registeredAddressSchema(t: TFunction<"validation">) {
  return z.object({
    registered_street: requiredTrimmedString(t, t("fields.street"), 1, 255),
    registered_postal_code: requiredTrimmedString(
      t,
      t("fields.postalCode"),
      1,
      20,
    ),
    registered_city: requiredTrimmedString(t, t("fields.city"), 1, 100),
    registered_province: requiredTrimmedString(t, t("fields.province"), 1, 100),
    registered_country: requiredTrimmedString(t, t("fields.country"), 1, 100),
  });
}

function identityDocumentSchema(t: TFunction<"validation">) {
  return z.object({
    document_type: requiredTrimmedString(t, t("fields.documentType"), 1, 50),
    document_number: requiredTrimmedString(t, t("fields.documentNumber"), 1, 50),
    document_issuer: requiredTrimmedString(t, t("fields.documentIssuer"), 1, 100),
    document_issue_date: requiredDocumentDateSchema(
      t,
      t("fields.documentIssueDate"),
    ),
    document_expiry_date: requiredDocumentDateSchema(
      t,
      t("fields.documentExpiryDate"),
    ),
  });
}

/** Expiry on/after issue for YYYY-MM-DD strings (lexicographic order). */
function documentExpiryAfterIssueRefine(t: TFunction<"validation">) {
  return (
    data: { document_issue_date: string; document_expiry_date: string },
    ctx: z.RefinementCtx,
  ) => {
    const issue = data.document_issue_date?.trim();
    const exp = data.document_expiry_date?.trim();
    if (!issue || !exp || issue.localeCompare(exp) <= 0) return;
    ctx.addIssue({
      code: "custom",
      path: ["document_expiry_date"],
      message: t("documentExpiryBeforeIssue"),
    });
  };
}

/** Tax ID / VAT-like: required, max 20 chars after trim + uppercase (aligned with backend). */
function requiredTaxLikeId(t: TFunction<"validation">, fieldLabel: string) {
  return z
    .string()
    .transform((s) => s.trim().toUpperCase())
    .superRefine((val, ctx) => {
      if (!val) {
        ctx.addIssue({
          code: "custom",
          message: t("fieldRequired", { field: fieldLabel }),
        });
        return;
      }
      if (val.length > 20) {
        ctx.addIssue({
          code: "custom",
          message: t("fieldMaxChars", { field: fieldLabel, max: 20 }),
        });
      }
    });
}

function requiredEmailField(
  t: TFunction<"validation">,
  label: string,
  max = 255,
) {
  return z
    .string()
    .transform((s) => s.trim().toLowerCase())
    .pipe(
      z
        .string()
        .min(1, t("fieldRequired", { field: label }))
        .max(max, t("fieldMaxChars", { field: label, max }))
        .pipe(z.email({ error: t("invalidEmailFormat") })),
    );
}

function requiredPhoneField(t: TFunction<"validation">) {
  const label = t("fields.contactPhone");
  return z
    .string()
    .transform((s) => s.trim())
    .pipe(
      z
        .string()
        .min(1, t("fieldRequired", { field: label }))
        .max(CONTACT_PHONE_MAX_LEN, t("fieldMaxChars", { field: label, max: CONTACT_PHONE_MAX_LEN }))
        .refine(
          (val) => isValidPhone(val, CONTACT_PHONE_MAX_LEN),
          t("invalidPhoneFormat"),
        )
        .transform((val) => normalizePhone(val) ?? val),
    );
}

export function contactSchema(
  t: TFunction<"validation">,
  fieldPolicy: LegalFieldPolicy,
) {
  const shared = {
    contact_phone: requiredPhoneField(t),
    contact_email: requiredEmailField(t, t("fields.contactEmail")),
    partner_notification_channel: z.enum(["WHATSAPP", "EMAIL"], {
      message: t("invalidNotificationChannel"),
    }),
    preferred_language: z.enum(SUPPORTED_LOCALES, {
      message: t("invalidNotificationLanguage"),
    }),
  };

  if (isFieldExcluded(fieldPolicy, "pec")) {
    return z.object(shared);
  }

  return z.object({
    ...shared,
    pec: requiredEmailField(t, t("fields.pec")),
  });
}

export function paymentSchema(
  t: TFunction<"validation">,
  fieldPolicy: LegalFieldPolicy,
) {
  if (
    isFieldOptional(fieldPolicy, "iban") &&
    isFieldOptional(fieldPolicy, "account_holder")
  ) {
    return z.object({
      iban: z
        .string()
        .transform((s) => s.replace(/\s/g, "").toUpperCase())
        .refine(
          (val) =>
            !val ||
            (/^[A-Z0-9]+$/.test(val) && val.length >= 15 && val.length <= 34),
          t("ibanLength"),
        ),
      account_holder: z
        .string()
        .transform((s) => s.trim())
        .refine(
          (val) => !val || (val.length >= 3 && val.length <= 255),
          t("fieldMinChars", { field: t("fields.accountHolder"), min: 3 }),
        ),
    });
  }

  return z.object({
    iban: z
      .string()
      .transform((s) => s.replace(/\s/g, "").toUpperCase())
      .refine(
        (val) => /^[A-Z0-9]+$/.test(val) && val.length >= 15 && val.length <= 34,
        t("ibanLength"),
      ),
    account_holder: requiredTrimmedString(
      t,
      t("fields.accountHolder"),
      3,
      255,
    ),
  });
}

export function createPersonPersonalSchema(t: TFunction<"validation">) {
  return z.object({
    first_name: requiredTrimmedString(t, t("fields.firstName"), 2, 100),
    last_name: requiredTrimmedString(t, t("fields.lastName"), 2, 100),
    birth_date: requiredBirthDateSchema(t),
    birth_place: requiredTrimmedString(t, t("fields.birthPlace"), 1, 100),
    fiscal_code: requiredTaxLikeId(t, t("fields.taxId")),
  });
}

export function createIdentityDocumentSchema(t: TFunction<"validation">) {
  return identityDocumentSchema(t).superRefine(documentExpiryAfterIssueRefine(t));
}

export function createCompanyBusinessSchema(t: TFunction<"validation">) {
  return z.object({
    company_name: requiredTrimmedString(t, t("fields.companyName"), 2, 255),
    vat_number: requiredTaxLikeId(t, t("fields.vatNumber")),
    fiscal_code: requiredTaxLikeId(t, t("fields.companyTaxId")),
  });
}

export function createCompanyLegalRepSchema(t: TFunction<"validation">) {
  return z
    .object({
      legal_rep_name: requiredTrimmedString(
        t,
        t("fields.legalRepName"),
        3,
        200,
      ),
    })
    .extend(identityDocumentSchema(t).shape)
    .superRefine(documentExpiryAfterIssueRefine(t));
}
